import { autoinject, transient } from "aurelia-framework";
import { Router } from "aurelia-router";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { Claims } from "../../../../constants/authentication/claims";
import { RightShipDialogServiceConstants } from "../../../../constants/basics/rightship-dialog.constants";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { ActiveReportSubscriptionReportModel, PurchasableReportModel, PurchasedReportModel } from "../../../../models/reports/reports.models";
import { CompRightStoreProductModel } from "../../../../models/rightstore/rightstore.models";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ManageRightShipUsersClientService } from "../../../../services/modules/righship-qi-manage-users.service";
import { ReportsService } from "../../../../services/reports/reports.service";
import { RightStoreService } from "../../../../services/rightstore/rightstore.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../../configuration/event-messages.constants";
import { CustomerService } from "../../../../services/modules/customer-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject()
@transient()
export class RightshipInsights {

    public activeReportSubscriptions: ActiveReportSubscriptionReportModel[];
    public purchasableReports: PurchasableReportModel[];
    public pendingMetaReports: PurchasedReportModel[];
    public purchasedReports: PurchasedReportModel[];
    public isRightShipUser: boolean;
    public isRightStoreEnabled: boolean;
    public showInsightsBetaAndFreeTrialBanner: boolean;
    public customerId: number;
    public previousCustomerId: number;
    public cacheKey: string = "insights-customer";

    constructor(private reportsService: ReportsService, private router: Router, private userClaimsService: UserClaimsService,
        private cacheClient: CacheClientService, private userClientService: UserClientService,
        private manageRightShipUsersClientService: ManageRightShipUsersClientService,
        private rightstoreService: RightStoreService,
        private dialogClientService: DialogClientService,
        private customerService: CustomerService,
        private commonCustomerService: CommonCustomerService,
        private eventAggregator: EventAggregator,
        private configService: ConfigService) {
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.showInsightsBetaAndFreeTrialBanner = this.userClaimsService.hasPermission(Claims.ShowInsightsBetaAndFreeTrialBanner);
        this.isRightStoreEnabled = this.userClaimsService.hasPermission(Claims.IsRightStoreEnabled);
    }

    async attached() {
        const response = await this.reportsService.getReports(null, null);
        this.activeReportSubscriptions = response.activeReportSubscriptions;
        this.purchasableReports = response.purchasableReports;
        this.pendingMetaReports = response.pendingMetaReports;
        this.purchasedReports = response.purchasedReports;
        await this.loadCachedCustomerSetting();
    }

    applyCustomerFilter() {

        //If a new customer is selected, clear business unit filters
        if (this.previousCustomerId !== this.customerId) {
            //set new customer to previousCustomerId
            const cust = Object.assign({}, { customerId: this.customerId });
            this.previousCustomerId = cust.customerId;
            if (this.isRightShipUser) {
                this.cacheClient.put(this.cacheKey, { customerId: this.customerId });
            }
        }
    }

    public async loadCachedCustomerSetting(): Promise<number> {
        if (!this.customerId) {
            if (this.isRightShipUser) {
                const response = await this.cacheClient.get<{ customerId: number }>(this.cacheKey, CacheLocation.LocalStorage, false);
                if (response) {
                    this.customerId = response.customerId;
                }
            } else {
                const user = await this.manageRightShipUsersClientService.getBusinessUnitUser(this.userClientService.currentUser.id);
                this.customerId = user && user.businessUnit ? user.businessUnit.customerId : null;
            }
        }
        return this.customerId;
    }

    openReport(report: ActiveReportSubscriptionReportModel) {
        if (report.isComingSoon) return;
        const shouldSendEntity = report.reportName == "Vetting Summary";
        this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.PowerBiReport, {
            id: report.reportId,
            title: report.reportName,
            entityTypeId: shouldSendEntity ? EntityTypes.Customer : null,
            entityId: this.isRightShipUser ? null : this.customerId //Non selected for Rightshipuser
        });
    }

    getActiveSubProductModel(item: ActiveReportSubscriptionReportModel): CompRightStoreProductModel {
        return {
            description: item.description,
            imageUrl: item.imageUrl,
            name: item.reportName,
            tags: [],
            url: null,
            isComingSoon: item.isComingSoon,
            expiry: null,
            showInPlatform: true
        }
    }

    getPurchasedProductModel(item: PurchasedReportModel): CompRightStoreProductModel {
        return {
            description: item.description,
            imageUrl: item.imageUrl,
            name: item.name,
            tags: [],
            url: item.url,
            isComingSoon: false,
            expiry: item.expiryDate,
            showInPlatform: item.showInPlatform
        }
    }

    onPendingProductClicked(purchasedReport: PurchasedReportModel) {

        //Check if the user clicking the product is the same that purchased it
        if (this.userClientService.currentUser.email == purchasedReport.purchasedByEmail) {
            this.navigateToOrderPage(purchasedReport);
        }
        else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PurchasedByAnotherUserDialog,
                {
                    purchasedBy: purchasedReport.purchasedBy,
                    proceed: () => {
                        this.navigateToOrderPage(purchasedReport)
                    }
                }, true);
        }
    }

    onPurchasedProductClicked(purchasedReport: PurchasedReportModel) {
        this.eventAggregator.publish(Messages.buttonClick, { "buttonName": purchasedReport.name, "functionName": "onPurchasedProductClicked" })

        this.navigateToOrderPage(purchasedReport);
    }

    navigateToOrderPage(purchasedReport: PurchasedReportModel) {
        if (!purchasedReport.showInPlatform) {
            return;
        }
        this.rightstoreService.getOrderUrl(purchasedReport.orderGuid).then(response => {
            location.href = response.url;
        });
    }

    getPurchasableProductModel(item: PurchasableReportModel): CompRightStoreProductModel {
        return {
            description: item.description,
            imageUrl: item.imageUrl,
            name: item.name,
            tags: [],
            url: item.url,
            isComingSoon: item.isComingSoon,
            expiry: null,
            showInPlatform: true
        }
    }

    async purchaseReportProduct(purchasableReport: PurchasableReportModel) {
        this.eventAggregator.publish(Messages.buttonClick, { "buttonName": purchasableReport.name, "functionName": "navigateToProductPage" })

        let customerId = this.userClientService.currentUser.settings.customerId;
        if (customerId == null || customerId <= 0) {
            customerId = await this.commonCustomerService.getCustomerId();
        }

        let businessUnitId = this.userClientService.currentUser.settings.businessUnitId;
        if (businessUnitId == null || businessUnitId <= 0) {
            businessUnitId = await this.commonCustomerService.getBusinessUnitId();
        }

        const queryParams = this.configService.config.environment.toLowerCase() == "production"
            ? ""
            : `env=${this.configService.config.environment}`;

        const customerModel = customerId > 0 ? await this.customerService.getCustomer(customerId) : null;

        if (purchasableReport.isInAppPurchaseEnabled && !purchasableReport.isComingSoon) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightstoreCheckoutDialog,
                {
                    productNumber: purchasableReport.productCode,
                    queryParams: queryParams,
                    customerNumber: businessUnitId,
                    userName: this.userClientService.currentUser.email,
                    canCustomerInvoice: customerModel?.customer?.canInvoice ?? true
                }, true, false, false, 'right');
        } else {
            if (purchasableReport.url) {
                window.open(purchasableReport.url, "_self");
            }
        }
    }
}